import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Headset, CheckCircle2, UserCog, Users as UsersIcon, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatCard } from "@/components/kit/StatCard";
import { SearchInput } from "@/components/kit/SearchInput";
import { FilterShell } from "@/components/kit/FilterShell";
import { StatusBadge } from "@/components/kit/StatusBadge";
import { DataTable } from "@/components/tables/DataTable";
import { RowActions } from "@/components/tables/RowActions";
import { EmptyState } from "@/components/feedback/EmptyState";
import { TableSkeleton } from "@/components/feedback/Skeletons";
import { ConfirmDialog } from "@/components/feedback/ConfirmDialog";
import { InviteLinkDialog } from "@/components/forms/InviteLinkDialog";
import { VaAgencyDrawer } from "./VaAgencyDrawer";
import { vaAgenciesService, warehousesService } from "@/services/resourceServices";
import { formatApiErrorDetail } from "@/services/api";
import { formatRelativeTime } from "@/lib/format";
import { isOwnerRole } from "@/lib/permissions/permissions";
import { useAuth } from "@/hooks/useAuth";

const DEFAULT_FILTERS = { search: "", status: "all" };

export default function VaAgenciesPage() {
  const qc = useQueryClient();
  const [draft, setDraft] = useState(DEFAULT_FILTERS);
  const [applied, setApplied] = useState(DEFAULT_FILTERS);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [invite, setInvite] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const params = useMemo(
    () => ({ search: applied.search, status: applied.status === "all" ? "" : applied.status }),
    [applied]
  );

  const listQuery = useQuery({ queryKey: ["va-agencies", params], queryFn: () => vaAgenciesService.list(params) });
  const summaryQuery = useQuery({ queryKey: ["va-agencies", "summary"], queryFn: vaAgenciesService.summary });
  const { user: currentUser } = useAuth();
  const isOwner = isOwnerRole(currentUser?.role);
  const warehousesQuery = useQuery({
    queryKey: ["warehouses", "lite"],
    queryFn: () => warehousesService.list(),
    enabled: isOwner,
  });
  const warehouses = useMemo(() => warehousesQuery.data?.items || [], [warehousesQuery.data]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["va-agencies"] });
    qc.invalidateQueries({ queryKey: ["activity"] });
  };

  const openCreate = () => { setEditing(null); setDrawerOpen(true); };
  const openEdit = (row) => { setEditing(row); setDrawerOpen(true); };

  const handleSaved = (res) => {
    refresh();
    if (res?.invite_link) setInvite({ link: res.invite_link, name: res.agency?.name, email: res.user?.email });
  };

  const confirmDelete = async () => {
    try {
      await vaAgenciesService.remove(toDelete.id);
      toast.success("Agency deleted");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setToDelete(null);
    }
  };

  const s = summaryQuery.data || {};
  const rows = listQuery.data?.items || [];

  const columns = [
    { key: "name", header: "Agency", render: (r) => <p className="font-medium text-foreground">{r.name}</p> },
    { key: "managers", header: "Managers", render: (r) => r.managers },
    { key: "staff", header: "Staff", render: (r) => r.staff },
    { key: "stores", header: "Stores", render: (r) => r.stores },
    {
      key: "status",
      header: "Status",
      render: (r) => <StatusBadge label={r.status === "active" ? "Active" : "Inactive"} tone={r.status === "active" ? "green" : "neutral"} />,
    },
    { key: "created", header: "Created", render: (r) => formatRelativeTime(r.created_at) },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (r) => (
        <RowActions
          items={[
            { key: "edit", label: "Edit", icon: Pencil, onSelect: () => openEdit(r) },
            { key: "delete", label: "Delete", icon: Trash2, destructive: true, separatorBefore: true, onSelect: () => setToDelete(r) },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="space-y-6" data-testid="page-va-agencies">
      <PageHeader
        sticky
        title="VA Agencies"
        description="Manage virtual assistant agencies, their owners, managers and staff."
        actions={
          <Button className="gap-2" onClick={openCreate} data-testid="va-agencies-primary-action">
            <Plus className="h-4 w-4" />
            New Agency
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard testId="stat-total-agencies" label="Total Agencies" value={s.total ?? "—"} icon={Headset} tone="blue" />
        <StatCard testId="stat-active-agencies" label="Active Agencies" value={s.active ?? "—"} icon={CheckCircle2} tone="green" />
        <StatCard testId="stat-total-managers" label="VA Managers" value={s.managers ?? "—"} icon={UserCog} tone="amber" />
        <StatCard testId="stat-total-staff" label="VA Staff" value={s.staff ?? "—"} icon={UsersIcon} tone="neutral" />
      </div>

      <FilterShell
        onApply={() => { setApplied(draft); toast.success("Filters applied"); }}
        onReset={() => { setDraft(DEFAULT_FILTERS); setApplied(DEFAULT_FILTERS); toast("Filters reset"); }}
      >
        <SearchInput value={draft.search} onChange={(v) => setDraft((d) => ({ ...d, search: v }))} placeholder="Search agencies…" />
        <Select value={draft.status} onValueChange={(v) => setDraft((d) => ({ ...d, status: v }))}>
          <SelectTrigger className="h-10 bg-background" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </FilterShell>

      {listQuery.isLoading ? (
        <TableSkeleton rows={5} cols={6} />
      ) : (
        <DataTable
          testId="va-agencies-table"
          paginated
          columns={columns}
          data={rows}
          emptyState={
            <EmptyState
              icon={Headset}
              title="No VA agencies yet"
              description="Create a VA agency to organize owners, managers and staff who work your stores."
              testId="va-agencies-empty-state"
              action={
                <Button className="gap-2" onClick={openCreate} data-testid="va-agencies-empty-action">
                  <Plus className="h-4 w-4" /> New Agency
                </Button>
              }
            />
          }
        />
      )}

      <VaAgencyDrawer open={drawerOpen} onOpenChange={setDrawerOpen} record={editing} warehouses={warehouses} onSaved={handleSaved} />
      <InviteLinkDialog open={!!invite} onOpenChange={(o) => !o && setInvite(null)} link={invite?.link} name={invite?.name} email={invite?.email} />
      <ConfirmDialog
        open={!!toDelete}
        onOpenChange={(o) => !o && setToDelete(null)}
        title="Delete agency?"
        description={`This permanently removes "${toDelete?.name}" and is recorded in the Activity Log.`}
        confirmLabel="Delete agency"
        destructive
        onConfirm={confirmDelete}
      />
    </div>
  );
}
